/**
 * Контракты API графика (раздел 6.2 ТЗ): график проекта, ИСР, работы, связи, календари,
 * базовые планы, правки пакетом с отменой и повтором, проверка качества, обмен файлами.
 */
import { z } from 'zod';

export const ACTIVITY_TYPES = ['task', 'start_ms', 'finish_ms', 'loe', 'hammock'] as const;
export const ACTIVITY_TYPE_TITLES: Record<(typeof ACTIVITY_TYPES)[number], string> = {
  task: 'Работа',
  start_ms: 'Веха начала',
  finish_ms: 'Веха окончания',
  loe: 'Работа уровня усилий',
  hammock: 'Гамак',
};
export const RELATION_TYPES = ['FS', 'SS', 'FF', 'SF'] as const;
export const RELATION_TITLES: Record<(typeof RELATION_TYPES)[number], string> = {
  FS: 'Окончание-начало',
  SS: 'Начало-начало',
  FF: 'Окончание-окончание',
  SF: 'Начало-окончание',
};
export const RELATION_SHORT: Record<(typeof RELATION_TYPES)[number], string> = {
  FS: 'ОН',
  SS: 'НН',
  FF: 'ОО',
  SF: 'НО',
};
export const CONSTRAINT_TYPES = ['asap', 'alap', 'snet', 'snlt', 'fnet', 'fnlt', 'mso', 'mfo'] as const;
export const CONSTRAINT_TITLES: Record<(typeof CONSTRAINT_TYPES)[number], string> = {
  asap: 'Как можно раньше',
  alap: 'Как можно позже',
  snet: 'Начало не ранее',
  snlt: 'Начало не позднее',
  fnet: 'Окончание не ранее',
  fnlt: 'Окончание не позднее',
  mso: 'Обязательное начало',
  mfo: 'Обязательное окончание',
};
/** Жёсткие ограничения для проверки DCMA (пункт 5) */
export const HARD_CONSTRAINTS = ['mso', 'mfo', 'snlt', 'fnlt'] as const;
export const LEVELS = ['directive', 'general', 'detailed', 'weekly'] as const;
export const LEVEL_TITLES: Record<(typeof LEVELS)[number], string> = {
  directive: 'Директивный (вехи)',
  general: 'Генеральный',
  detailed: 'Детальный',
  weekly: 'Недельно-суточный',
};
export const WBS_KINDS = ['object', 'section', 'structure', 'stage', 'other'] as const;
export const WBS_KIND_TITLES: Record<(typeof WBS_KINDS)[number], string> = {
  object: 'Объект',
  section: 'Раздел (шифр РП)',
  structure: 'Конструктив',
  stage: 'Этап',
  other: 'Группа',
};
export const LAG_CALENDARS = ['pred', 'succ', '24h', 'project'] as const;
export const LAG_CALENDAR_TITLES: Record<(typeof LAG_CALENDARS)[number], string> = {
  pred: 'Календарь предшественника',
  succ: 'Календарь последователя',
  '24h': 'Календарные сутки (24 ч)',
  project: 'Календарь проекта',
};

/** Момент «ГГГГ-ММ-ДДTЧЧ:ММ» или день «ГГГГ-ММ-ДД» */
export const Moment = z
  .string()
  .regex(
    /^(19[5-9]\d|20\d\d|21[0-4]\d)-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(T([01]\d|2[0-3]):[0-5]\d)?$/,
    'Дата в формате ГГГГ-ММ-ДД или ГГГГ-ММ-ДДTЧЧ:ММ, год от 1950 до 2149',
  );
const Day = z
  .string()
  .regex(
    /^(19[5-9]\d|20\d\d|21[0-4]\d)-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
    'Дата в формате ГГГГ-ММ-ДД, год от 1950 до 2149',
  );
const Interval = z.tuple([z.number().int().min(0).max(1440), z.number().int().min(0).max(1440)]);
const DayIv = z.array(Interval).max(6);

export const CalendarDefSchema = z.object({
  week: z.array(DayIv).length(7),
  rotation: z
    .object({
      on: z.number().int().min(1).max(90),
      off: z.number().int().min(0).max(90),
      anchor: Day,
      day: DayIv,
    })
    .nullable()
    .optional(),
  holidays: z.enum(['kz', 'none']),
  exceptions: z
    .array(
      z.object({
        from: Day,
        to: Day,
        work: z.boolean(),
        day: DayIv.nullable().optional(),
        note: z.string().max(200).nullable().optional(),
      }),
    )
    .max(500),
  seasonal: z
    .array(
      z.object({
        from: z.string().regex(/^\d{2}-\d{2}$/),
        to: z.string().regex(/^\d{2}-\d{2}$/),
        note: z.string().max(200).nullable().optional(),
      }),
    )
    .max(20),
  hoursPerDay: z.number().min(0.5).max(24),
});

export const ScheduleCalendarRow = z.object({
  id: z.string(),
  projectId: z.string().nullable(),
  name: z.string(),
  preset: z.string().nullable(),
  def: CalendarDefSchema,
});
export type ScheduleCalendarRow = z.infer<typeof ScheduleCalendarRow>;

export const CalendarInput = z.object({
  projectId: z.uuid().nullable().optional(),
  name: z.string().trim().min(1).max(120),
  preset: z.string().max(40).nullable().optional(),
  def: CalendarDefSchema,
});

export const ScheduleSettings = z.object({
  outOfSequence: z.enum(['retained', 'override']).default('retained'),
  floatFrom: z.enum(['project', 'open_ends']).default('project'),
  floatType: z.enum(['finish', 'start', 'smallest']).default('finish'),
  lagCalendar: z.enum(LAG_CALENDARS).default('pred'),
  criticalBy: z.enum(['float', 'longest']).default('float'),
  /** Порог критичности и почти критических работ — в рабочих днях календаря проекта */
  criticalFloatDays: z.number().min(0).max(100).default(0),
  nearCriticalDays: z.number().min(0).max(200).default(5),
  /** Утверждённый график возвращается в работу только по утверждённому запросу на изменение (ПЛ-24) */
  reopenByChange: z.boolean().default(false),
});
export type ScheduleSettings = z.infer<typeof ScheduleSettings>;
export const DEFAULT_SCHEDULE_SETTINGS: ScheduleSettings = ScheduleSettings.parse({});

export const ActivityDates = z.object({
  es: z.string(),
  ef: z.string(),
  ls: z.string(),
  lf: z.string(),
  tf: z.number().nullable(),
  ff: z.number().nullable(),
  remaining: z.number(),
  critical: z.boolean(),
  nearCritical: z.boolean(),
  longestPath: z.boolean(),
  status: z.enum(['not_started', 'in_progress', 'complete']),
});
export type ActivityDates = z.infer<typeof ActivityDates>;

export const ActivityRow = z.object({
  id: z.string(),
  wbsId: z.string().nullable(),
  code: z.string(),
  name: z.string(),
  type: z.enum(ACTIVITY_TYPES),
  calendarId: z.string().nullable(),
  durationMin: z.number(),
  remainingMin: z.number().nullable(),
  durationMode: z.enum(['fixed', 'quantity']),
  quantity: z.number().nullable(),
  unit: z.string().nullable(),
  ratePerDay: z.number().nullable(),
  crews: z.number().nullable(),
  constraintType: z.enum(CONSTRAINT_TYPES),
  constraintDate: z.string().nullable(),
  deadline: z.string().nullable(),
  actualStart: z.string().nullable(),
  actualFinish: z.string().nullable(),
  percent: z.number(),
  percentType: z.enum(['duration', 'units', 'physical']),
  actualQuantity: z.number().nullable(),
  level: z.enum(LEVELS),
  responsibleId: z.string().nullable(),
  contractorId: z.string().nullable(),
  contractId: z.string().nullable(),
  zoneId: z.string().nullable(),
  workType: z.string().nullable(),
  note: z.string().nullable(),
  sortOrder: z.number(),
  priority: z.number().nullable().optional(),
  durationOptMin: z.number().nullable().optional(),
  durationPessMin: z.number().nullable().optional(),
  locationFrom: z.number().nullable().optional(),
  locationTo: z.number().nullable().optional(),
  codes: z.record(z.string(), z.string()),
  /** Кто сейчас правит работу (ПЛ-31) */
  lockedBy: z.object({ id: z.string(), name: z.string(), until: z.string() }).nullable(),
  dates: ActivityDates.nullable(),
  updatedAt: z.string(),
});
export type ActivityRow = z.infer<typeof ActivityRow>;

export const ActivityInput = z.object({
  wbsId: z.uuid().nullable().optional(),
  code: z.string().trim().min(1).max(40).optional(),
  name: z.string().trim().min(1).max(300).optional(),
  type: z.enum(ACTIVITY_TYPES).optional(),
  calendarId: z.uuid().nullable().optional(),
  durationMin: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 365 * 20)
    .optional(),
  remainingMin: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 365 * 20)
    .nullable()
    .optional(),
  durationMode: z.enum(['fixed', 'quantity']).optional(),
  quantity: z.number().min(0).max(1e12).nullable().optional(),
  unit: z.string().max(20).nullable().optional(),
  ratePerDay: z.number().min(0).max(1e12).nullable().optional(),
  crews: z.number().int().min(1).max(1000).nullable().optional(),
  constraintType: z.enum(CONSTRAINT_TYPES).optional(),
  constraintDate: Moment.nullable().optional(),
  deadline: Moment.nullable().optional(),
  actualStart: Moment.nullable().optional(),
  actualFinish: Moment.nullable().optional(),
  percent: z.number().min(0).max(100).optional(),
  percentType: z.enum(['duration', 'units', 'physical']).optional(),
  actualQuantity: z.number().min(0).max(1e12).nullable().optional(),
  level: z.enum(LEVELS).optional(),
  responsibleId: z.uuid().nullable().optional(),
  contractorId: z.uuid().nullable().optional(),
  contractId: z.uuid().nullable().optional(),
  zoneId: z.uuid().nullable().optional(),
  workType: z.string().max(80).nullable().optional(),
  note: z.string().max(4000).nullable().optional(),
  sortOrder: z.number().int().optional(),
  /** Приоритет при выравнивании: 1 — сдвигать последней, 5 — первой (ПЛ-14) */
  priority: z.number().int().min(1).max(5).nullable().optional(),
  /** Три оценки длительности для вероятностного анализа (ПЛ-23), рабочих минут */
  durationOptMin: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 365 * 20)
    .nullable()
    .optional(),
  durationPessMin: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 365 * 20)
    .nullable()
    .optional(),
  /** Пикеты начала и конца на линейном объекте, метров (ПЛ-27) */
  locationFrom: z.number().min(-1e7).max(1e7).nullable().optional(),
  locationTo: z.number().min(-1e7).max(1e7).nullable().optional(),
});
export type ActivityInput = z.infer<typeof ActivityInput>;

export const RelationRow = z.object({
  id: z.string(),
  predId: z.string(),
  succId: z.string(),
  type: z.enum(RELATION_TYPES),
  lagMin: z.number(),
  lagUnit: z.enum(['d', 'h', 'pct']),
  lagPct: z.number().nullable(),
  lagCalendar: z.enum(LAG_CALENDARS).nullable(),
  slack: z.number().nullable(),
  driving: z.boolean(),
});
export type RelationRow = z.infer<typeof RelationRow>;

export const WbsRow = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  kind: z.enum(WBS_KINDS),
  objectId: z.string().nullable(),
  sectionCode: z.string().nullable(),
  sortOrder: z.number(),
});
export type WbsRow = z.infer<typeof WbsRow>;

export const WbsInput = z.object({
  parentId: z.uuid().nullable().optional(),
  code: z.string().trim().max(40).nullable().optional(),
  name: z.string().trim().min(1).max(300).optional(),
  kind: z.enum(WBS_KINDS).optional(),
  objectId: z.uuid().nullable().optional(),
  sectionCode: z.string().trim().max(80).nullable().optional(),
  sortOrder: z.number().int().optional(),
});

/** Правки графика пакетом: одна транзакция, один пересчёт; отмена — обратный пакет */
export const ScheduleOp = z.discriminatedUnion('op', [
  z.object({ op: z.literal('activity'), id: z.uuid(), create: z.boolean().optional(), data: ActivityInput }),
  z.object({ op: z.literal('activity.delete'), id: z.uuid() }),
  z.object({
    op: z.literal('relation'),
    id: z.uuid(),
    create: z.boolean().optional(),
    predId: z.uuid(),
    succId: z.uuid(),
    type: z.enum(RELATION_TYPES),
    lagMin: z
      .number()
      .int()
      .min(-60 * 24 * 365 * 5)
      .max(60 * 24 * 365 * 5),
    lagUnit: z.enum(['d', 'h', 'pct']).optional(),
    lagPct: z.number().min(-1000).max(1000).nullable().optional(),
    lagCalendar: z.enum(LAG_CALENDARS).nullable().optional(),
  }),
  z.object({ op: z.literal('relation.delete'), id: z.uuid() }),
  z.object({ op: z.literal('wbs'), id: z.uuid(), create: z.boolean().optional(), data: WbsInput }),
  z.object({ op: z.literal('wbs.delete'), id: z.uuid() }),
  z.object({ op: z.literal('code'), activityId: z.uuid(), typeId: z.uuid(), valueId: z.uuid().nullable() }),
]);
export type ScheduleOp = z.infer<typeof ScheduleOp>;
export const ScheduleChanges = z.object({
  ops: z.array(ScheduleOp).min(1).max(20_000),
  /** Номер правки, на которой человек видел график: для предупреждения о чужих правках */
  revision: z.number().int().optional(),
  /** Подпись правки для журнала отката: «Длительность A240», «Новая работа» */
  label: z.string().max(200).optional(),
  /** Ответ — только изменённое (ScheduleDelta), если правка не меняет состав графика (ПМ-08) */
  delta: z.boolean().optional(),
});

export const BaselineRow = z.object({
  id: z.string(),
  number: z.number(),
  name: z.string(),
  isPrimary: z.boolean(),
  dataDate: z.string().nullable(),
  start: z.string().nullable(),
  finish: z.string().nullable(),
  activityCount: z.number(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type BaselineRow = z.infer<typeof BaselineRow>;

export const CodeTypeRow = z.object({
  id: z.string(),
  scheduleId: z.string().nullable(),
  name: z.string(),
  values: z.array(
    z.object({
      id: z.string(),
      value: z.string(),
      title: z.string().nullable(),
      color: z.string().nullable(),
    }),
  ),
});
export type CodeTypeRow = z.infer<typeof CodeTypeRow>;

export const ZoneRow = z.object({
  id: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  objectId: z.string().nullable(),
  note: z.string().nullable(),
});
export type ZoneRow = z.infer<typeof ZoneRow>;

export const ScheduleConflict = z.object({ activityId: z.string(), kind: z.string(), message: z.string() });

export const ScheduleHead = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  status: z.enum(['draft', 'approved']),
  calendarId: z.string().nullable(),
  startDate: z.string(),
  dataDate: z.string().nullable(),
  mustFinishBy: z.string().nullable(),
  settings: ScheduleSettings,
  revision: z.number(),
  calcRevision: z.number().nullable(),
  calcAt: z.string().nullable(),
  calcMs: z.number().nullable(),
  calcFinish: z.string().nullable(),
  approvedAt: z.string().nullable(),
  /** Последний возврат утверждённого графика в работу: причина и запрос на изменение (ПЛ-24) */
  reopen: z
    .object({
      reason: z.string(),
      at: z.string(),
      change: z.object({ id: z.string(), number: z.string(), title: z.string() }).nullable(),
    })
    .nullable()
    .optional(),
  conflicts: z.array(ScheduleConflict),
  cycles: z.array(z.array(z.string())),
});
export type ScheduleHead = z.infer<typeof ScheduleHead>;

export const ScheduleView = z.object({
  schedule: ScheduleHead,
  project: z.object({ id: z.string(), code: z.string(), name: z.string(), legalEntityId: z.string() }),
  calendars: z.array(ScheduleCalendarRow),
  wbs: z.array(WbsRow),
  activities: z.array(ActivityRow),
  relations: z.array(RelationRow),
  /** Работы других проектов, связанные с этим графиком (ПЛ-05) */
  external: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      projectCode: z.string(),
      start: z.string(),
      finish: z.string(),
    }),
  ),
  baselines: z.array(BaselineRow),
  /** Даты основного базового плана: { работа: [начало, окончание] } */
  baselineDates: z.record(z.string(), z.tuple([z.string(), z.string()])),
  codeTypes: z.array(CodeTypeRow),
  zones: z.array(ZoneRow),
  people: z.array(z.object({ id: z.string(), name: z.string() })),
  contractors: z.array(z.object({ id: z.string(), name: z.string() })),
  objects: z.array(z.object({ id: z.string(), name: z.string() })),
  can: z.object({ edit: z.boolean(), baseline: z.boolean(), approve: z.boolean() }),
});
export type ScheduleView = z.infer<typeof ScheduleView>;

/**
 * Ответ правки графика — только изменённое (ПМ-08): шапка, работы с новыми данными или датами расчёта,
 * связи с новыми данными или новым запасом. Остальное в виде графика на странице не меняется.
 */
export const ScheduleDelta = z.object({
  delta: z.literal(true),
  schedule: ScheduleHead,
  activities: z.array(ActivityRow),
  relations: z.array(RelationRow),
});
export type ScheduleDelta = z.infer<typeof ScheduleDelta>;

/** Применить ответ правки к виду графика: те же работы и связи на своих местах, изменённые — новые */
export function mergeScheduleDelta(v: ScheduleView, d: ScheduleDelta): ScheduleView {
  const acts = new Map(d.activities.map((a) => [a.id, a]));
  const rels = new Map(d.relations.map((r) => [r.id, r]));
  return {
    ...v,
    schedule: d.schedule,
    activities: acts.size ? v.activities.map((a) => acts.get(a.id) ?? a) : v.activities,
    relations: rels.size ? v.relations.map((r) => rels.get(r.id) ?? r) : v.relations,
  };
}

export const ScheduleSummary = z.object({
  projectId: z.string(),
  projectCode: z.string(),
  projectName: z.string(),
  scheduleId: z.string().nullable(),
  status: z.string().nullable(),
  activities: z.number(),
  critical: z.number(),
  start: z.string().nullable(),
  finish: z.string().nullable(),
  baselineFinish: z.string().nullable(),
  /** Отклонение окончания от основного базового плана, рабочих дней (+ — позже) */
  slipDays: z.number().nullable(),
  dataDate: z.string().nullable(),
  progress: z.number().nullable(),
  calcAt: z.string().nullable(),
  problems: z.number(),
});
export type ScheduleSummary = z.infer<typeof ScheduleSummary>;

export const ScheduleCreate = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  startDate: Day,
  /** empty — пустой график, sample — пример (камера, водовод, резервуар) */
  from: z.enum(['empty', 'sample']).default('empty'),
  calendarPreset: z.string().max(40).default('5x2'),
});

export const ScheduleHeadInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  calendarId: z.uuid().nullable().optional(),
  startDate: Day.optional(),
  dataDate: Moment.nullable().optional(),
  mustFinishBy: Moment.nullable().optional(),
  settings: ScheduleSettings.partial().optional(),
});

export const BaselineInput = z.object({
  name: z.string().trim().min(1).max(200),
  note: z.string().max(2000).nullable().optional(),
  isPrimary: z.boolean().optional(),
});

/** Сравнение двух версий (ПЛ-16): базовые планы между собой или с текущим графиком */
export const BaselineCompare = z.object({
  a: z.object({ id: z.string(), name: z.string(), finish: z.string().nullable() }),
  b: z.object({ id: z.string(), name: z.string(), finish: z.string().nullable() }),
  rows: z.array(
    z.object({
      activityId: z.string(),
      code: z.string(),
      name: z.string(),
      /** added — есть только в b, removed — только в a, changed, same */
      change: z.enum(['added', 'removed', 'changed', 'same']),
      startA: z.string().nullable(),
      startB: z.string().nullable(),
      finishA: z.string().nullable(),
      finishB: z.string().nullable(),
      durationA: z.number().nullable(),
      durationB: z.number().nullable(),
      /** Сдвиг окончания b против a, рабочих дней календаря проекта */
      finishShiftDays: z.number().nullable(),
      costA: z.number().nullable(),
      costB: z.number().nullable(),
    }),
  ),
  totals: z.object({
    added: z.number(),
    removed: z.number(),
    changed: z.number(),
    finishShiftDays: z.number().nullable(),
  }),
});
export type BaselineCompare = z.infer<typeof BaselineCompare>;

/** Пункт проверки качества графика по DCMA (ПЛ-12) */
export const DcmaItem = z.object({
  no: z.number(),
  code: z.string(),
  title: z.string(),
  /** Значение показателя: процент, число или индекс */
  value: z.number().nullable(),
  unit: z.enum(['pct', 'count', 'index']),
  threshold: z.string(),
  pass: z.boolean().nullable(),
  hint: z.string(),
  activityIds: z.array(z.string()),
});
export type DcmaItem = z.infer<typeof DcmaItem>;
export const DcmaReport = z.object({
  total: z.number(),
  passed: z.number(),
  items: z.array(DcmaItem),
});
export type DcmaReport = z.infer<typeof DcmaReport>;

/** Отчёт импорта (ПЛ-33): что прочитано и какие расхождения */
export const ImportReport = z.object({
  format: z.string(),
  activities: z.number(),
  relations: z.number(),
  wbs: z.number(),
  calendars: z.number(),
  /** Сверка дат файла с расчётом портала: работы, у которых ранние даты разошлись */
  compared: z.number(),
  mismatches: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      field: z.string(),
      file: z.string(),
      portal: z.string(),
    }),
  ),
  /**
   * Критический путь (резерв не больше нуля): сколько работ критических в файле и в портале и какие
   * критические только там или только здесь. Сравниваются работы, где файл отдал резерв.
   */
  critical: z
    .object({
      compared: z.number(),
      file: z.number(),
      portal: z.number(),
      onlyFile: z.array(z.string()),
      onlyPortal: z.array(z.string()),
    })
    .optional(),
  /** Окончание проекта: самое позднее раннее окончание в файле и расчёт портала */
  fileFinish: z.string().nullable().optional(),
  portalFinish: z.string().nullable().optional(),
  warnings: z.array(z.string()),
  /**
   * Отпечаток графика, разобранного из PDF (ПЛ-33): загрузка в график идёт только с этим отпечатком —
   * человек подтверждает ровно тот разбор, который сверил
   */
  pdfHash: z.string().optional(),
});
export type ImportReport = z.infer<typeof ImportReport>;

/* ───────────────────────── Перевод длительностей ───────────────────────── */

/** Рабочие минуты → дни по часам в дне календаря */
export function minutesToDays(min: number | null | undefined, hoursPerDay: number): number | null {
  if (min === null || min === undefined) return null;
  return Math.round((min / (hoursPerDay * 60)) * 100) / 100;
}

export function daysToMinutes(days: number, hoursPerDay: number): number {
  return Math.round(days * hoursPerDay * 60);
}

/* ── Сценарии «что если» (ПЛ-20) ── */
export const SCENARIO_KINDS = {
  delay: 'Сдвиг начала (поставки) на дней',
  crews: 'Дополнительные бригады',
  duration: 'Другая длительность, раб. дней',
  calendar: 'Другой календарь',
  lag: 'Другой лаг связи, дней',
  remaining: 'Осталось у начатой работы, раб. дней',
} as const;
export const ScenarioChange = z.discriminatedUnion('kind', [
  /** Работа (поставка) начнётся не раньше, чем через столько календарных дней после раннего начала */
  z.object({ kind: z.literal('delay'), activityId: z.uuid(), days: z.number().int().min(-365).max(3650) }),
  /** Бригад больше на столько: длительность уменьшается пропорционально */
  z.object({ kind: z.literal('crews'), activityId: z.uuid(), add: z.number().int().min(1).max(50) }),
  z.object({ kind: z.literal('duration'), activityId: z.uuid(), days: z.number().min(0).max(3650) }),
  /** Сколько осталось у начатой работы — прогноз по выработке (ПЛ-18) */
  z.object({ kind: z.literal('remaining'), activityId: z.uuid(), days: z.number().min(0).max(3650) }),
  /** Календарь работы; без работы — для всех работ на основном календаре графика */
  z.object({ kind: z.literal('calendar'), activityId: z.uuid().nullable(), calendarId: z.uuid() }),
  z.object({ kind: z.literal('lag'), relationId: z.uuid(), days: z.number().min(-3650).max(3650) }),
]);
export type ScenarioChange = z.infer<typeof ScenarioChange>;
export const ScenarioInput = z.object({
  name: z.string().trim().min(1).max(200),
  note: z.string().max(2000).nullable().optional(),
  changes: z.array(ScenarioChange).max(500),
});
export const ScenarioRow = z.object({
  id: z.string(),
  name: z.string(),
  note: z.string().nullable(),
  changes: z.array(ScenarioChange),
  appliedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ScenarioRow = z.infer<typeof ScenarioRow>;
export const ScenarioResult = z.object({
  finishCurrent: z.string(),
  finish: z.string(),
  shiftDays: z.number(),
  mustFinishBy: z.string().nullable(),
  critical: z.number(),
  criticalCurrent: z.number(),
  milestones: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      current: z.string(),
      scenario: z.string(),
    }),
  ),
  /** Работы, у которых изменились даты: самые сдвинутые сверху */
  moved: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      startCurrent: z.string(),
      start: z.string(),
      finishCurrent: z.string(),
      finish: z.string(),
      shiftDays: z.number(),
    }),
  ),
  /** Изменения, которые не применить: работа или связь удалена */
  skipped: z.number(),
});
export type ScenarioResult = z.infer<typeof ScenarioResult>;

/* ── Правила начисления выполнения (ПЛ-36) ── */
export const STEP_CLOSERS = {
  manual: 'Вручную',
  act: 'Подписанный акт освидетельствования',
  report: 'Принятый отчёт смены',
  control: 'Точка плана контроля',
} as const;
export type StepCloser = keyof typeof STEP_CLOSERS;
const StepCloserEnum = z.enum(['manual', 'act', 'report', 'control']);
export const ProgressStep = z.object({
  name: z.string().trim().min(1).max(200),
  weight: z.number().min(0.01).max(100),
  closeBy: StepCloserEnum,
});
export const ProgressRuleInput = z
  .object({
    name: z.string().trim().min(1).max(200),
    workType: z.string().trim().max(80).nullable().optional(),
    steps: z.array(ProgressStep).min(1).max(30),
  })
  .refine((x) => Math.abs(x.steps.reduce((n, s) => n + s.weight, 0) - 100) < 0.01, {
    message: 'Сумма весов шагов должна быть 100 %',
    path: ['steps'],
  });
export const ProgressRuleRow = z.object({
  id: z.string(),
  name: z.string(),
  workType: z.string().nullable(),
  steps: z.array(ProgressStep),
});
export type ProgressRuleRow = z.infer<typeof ProgressRuleRow>;
export const ActivityStepRow = z.object({
  id: z.string(),
  idx: z.number(),
  name: z.string(),
  weight: z.number(),
  closeBy: StepCloserEnum,
  doneAt: z.string().nullable(),
  doneByName: z.string().nullable(),
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  /** Чем можно закрыть сейчас: подписанный акт по этой работе */
  evidence: z.object({ type: z.string(), id: z.string(), title: z.string() }).nullable(),
  note: z.string().nullable(),
});
export const ActivitySteps = z.object({
  ruleId: z.string().nullable(),
  ruleName: z.string().nullable(),
  steps: z.array(ActivityStepRow),
  /** Процент по закрытым шагам */
  percent: z.number(),
  /** Правило, которое подходит по виду работ, если шагов ещё нет */
  suggested: z.object({ id: z.string(), name: z.string() }).nullable(),
  canEdit: z.boolean(),
});
export type ActivitySteps = z.infer<typeof ActivitySteps>;

/* ── Недельно-суточный план (ПЛ-35) ── */
export const MISS_REASONS = {
  front: 'Нет фронта работ',
  material: 'Нет материала',
  people: 'Нет людей',
  equipment: 'Нет техники',
  design: 'Нет ответа проектировщика',
  weather: 'Погода',
  other: 'Другое',
} as const;
export type MissReason = keyof typeof MISS_REASONS;
export const COMMITMENT_STATUSES = {
  planned: 'Запланировано',
  committed: 'Подтверждено',
  done: 'Выполнено',
  missed: 'Не выполнено',
} as const;
export type CommitmentStatus = keyof typeof COMMITMENT_STATUSES;
const DayStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const CommitmentRow = z.object({
  id: z.string(),
  activityId: z.string(),
  code: z.string(),
  name: z.string(),
  week: z.string(),
  zoneId: z.string().nullable(),
  zoneName: z.string().nullable(),
  contractorId: z.string().nullable(),
  contractorName: z.string().nullable(),
  planStart: z.string(),
  planFinish: z.string(),
  scope: z.string().nullable(),
  status: z.enum(['planned', 'committed', 'done', 'missed']),
  committedByName: z.string().nullable(),
  committedAt: z.string().nullable(),
  replans: z.number(),
  replanReason: z.string().nullable(),
  missReason: z.enum(['front', 'material', 'people', 'equipment', 'design', 'weather', 'other']).nullable(),
  missNote: z.string().nullable(),
  /** Может подтвердить: правит график или отвечает за работу */
  canCommit: z.boolean(),
});
export type CommitmentRow = z.infer<typeof CommitmentRow>;
export const LookaheadRow = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  es: z.string(),
  ef: z.string(),
  critical: z.boolean(),
  zoneId: z.string().nullable(),
  contractorId: z.string().nullable(),
  responsibleId: z.string().nullable(),
  percent: z.number(),
  started: z.boolean(),
});
export const WeekPlan = z.object({
  week: z.string(),
  weeks: z.number(),
  lookahead: z.array(LookaheadRow),
  commitments: z.array(CommitmentRow),
  zones: z.array(z.object({ id: z.string(), name: z.string() })),
  contractors: z.array(z.object({ id: z.string(), name: z.string() })),
  canPlan: z.boolean(),
});
export type WeekPlan = z.infer<typeof WeekPlan>;
export const CommitmentsInput = z.object({
  week: DayStr,
  items: z
    .array(
      z.object({
        activityId: z.uuid(),
        planStart: DayStr.optional(),
        planFinish: DayStr.optional(),
        scope: z.string().max(500).nullable().optional(),
      }),
    )
    .min(1)
    .max(500),
});
export const CommitmentPatch = z.object({
  planStart: DayStr.optional(),
  planFinish: DayStr.optional(),
  scope: z.string().max(500).nullable().optional(),
  /** После подтверждения даты меняются только с причиной */
  replanReason: z.string().trim().min(3).max(500).optional(),
});
export const CommitmentClose = z.object({
  done: z.boolean(),
  missReason: z.enum(['front', 'material', 'people', 'equipment', 'design', 'weather', 'other']).optional(),
  missNote: z.string().max(500).nullable().optional(),
});
export const PpcReport = z.object({
  weeks: z.array(
    z.object({ week: z.string(), total: z.number(), done: z.number(), ppc: z.number().nullable() }),
  ),
  byContractor: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      total: z.number(),
      done: z.number(),
      ppc: z.number().nullable(),
    }),
  ),
  byZone: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      total: z.number(),
      done: z.number(),
      ppc: z.number().nullable(),
    }),
  ),
  reasons: z.array(z.object({ reason: z.string(), title: z.string(), count: z.number() })),
});
export type PpcReport = z.infer<typeof PpcReport>;

/* ── Пакеты работ и ограничения (ПЛ-38–40) ── */
export const CONSTRAINT_KINDS = {
  psd: 'Листы ПСД в производство работ',
  question: 'Ответ на вопрос к проекту',
  front: 'Фронт от предыдущей работы',
  act: 'Акт освидетельствования предыдущих работ',
  purchase: 'Поставка по заявке на закуп',
  contractor_docs: 'Согласованные документы подрядчика',
  material: 'Материалы на складе или в резерве',
  people: 'Люди с допусками',
  equipment: 'Техника',
  permit: 'Наряд-допуск',
  scaffold: 'Леса и подъёмники',
  access: 'Доступ на площадку',
  other: 'Другое',
} as const;
export type ConstraintKind = keyof typeof CONSTRAINT_KINDS;
/** Что ПТО отмечает вручную; остальное портал проверяет по связям */
export const MANUAL_CONSTRAINTS = [
  'contractor_docs',
  'material',
  'people',
  'equipment',
  'permit',
  'scaffold',
  'access',
  'other',
] as const;
/** Какой причиной невыполнения недели считается ограничение (ПЛ-40) */
export const CONSTRAINT_REASON: Record<ConstraintKind, MissReason> = {
  psd: 'design',
  question: 'design',
  front: 'front',
  act: 'front',
  purchase: 'material',
  contractor_docs: 'other',
  material: 'material',
  people: 'people',
  equipment: 'equipment',
  permit: 'people',
  scaffold: 'equipment',
  access: 'front',
  other: 'other',
};
export const PackageConstraintRow = z.object({
  /** id ручного ограничения; у найденного по связям — ключ «вид:запись» */
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  auto: z.boolean(),
  open: z.boolean(),
  activityCode: z.string().nullable(),
  link: z.object({ type: z.string(), id: z.string() }).nullable(),
  responsibleId: z.string().nullable(),
  responsibleName: z.string().nullable(),
  dueDate: z.string().nullable(),
  doneAt: z.string().nullable(),
  overdue: z.boolean(),
  note: z.string().nullable(),
  /** Может снять: ПТО или ответственный за ручное ограничение */
  canToggle: z.boolean(),
});
export type PackageConstraintRow = z.infer<typeof PackageConstraintRow>;
export const PackageRow = z.object({
  id: z.string(),
  number: z.string(),
  name: z.string(),
  zoneId: z.string().nullable(),
  zoneName: z.string().nullable(),
  workType: z.string().nullable(),
  contractorName: z.string().nullable(),
  foremanId: z.string().nullable(),
  foremanName: z.string().nullable(),
  dateFrom: z.string(),
  dateTo: z.string(),
  status: z.enum(['draft', 'issued', 'closed']),
  items: z.number(),
  openConstraints: z.number(),
  exceptionReason: z.string().nullable(),
});
export type PackageRow = z.infer<typeof PackageRow>;
export const PackageDetail = PackageRow.extend({
  note: z.string().nullable(),
  issuedByName: z.string().nullable(),
  issuedAt: z.string().nullable(),
  activities: z.array(
    z.object({
      itemId: z.string(),
      id: z.string(),
      code: z.string(),
      name: z.string(),
      es: z.string().nullable(),
      ef: z.string().nullable(),
      quantity: z.number().nullable(),
      unit: z.string().nullable(),
      manHours: z.number(),
      scope: z.string().nullable(),
      percent: z.number(),
    }),
  ),
  /** Листы ПСД, вопросы, заявки на закуп, люди и техника по назначениям */
  sets: z.array(z.object({ id: z.string(), title: z.string(), received: z.boolean() })),
  resources: z.array(z.object({ name: z.string(), kind: z.string(), total: z.number(), unit: z.string() })),
  constraints: z.array(PackageConstraintRow),
  canEdit: z.boolean(),
  /** Выдать с открытыми ограничениями может руководитель проекта (право на базовые планы) */
  canOverride: z.boolean(),
});
export type PackageDetail = z.infer<typeof PackageDetail>;
export const PackageInput = z.object({
  name: z.string().trim().min(1).max(200),
  zoneId: z.uuid().nullable().optional(),
  workType: z.string().trim().max(80).nullable().optional(),
  foremanId: z.uuid().nullable().optional(),
  dateFrom: DayStr,
  dateTo: DayStr,
  note: z.string().max(2000).nullable().optional(),
  activityIds: z.array(z.uuid()).max(300).optional(),
});
export const ConstraintInput = z.object({
  kind: z.enum(MANUAL_CONSTRAINTS),
  title: z.string().trim().min(1).max(300),
  responsibleId: z.uuid().nullable().optional(),
  dueDate: DayStr.nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
});
export const ConstraintLogRow = PackageConstraintRow.extend({
  packageId: z.string(),
  packageNumber: z.string(),
  packageName: z.string(),
});
export type ConstraintLogRow = z.infer<typeof ConstraintLogRow>;

/* ── Подбор варианта графика (ПЛ-41) ── */
export const VARIANT_CRITERIA = {
  finish: 'Раньше окончание',
  peak: 'Меньше пик людей и техники',
  smooth: 'Ровнее загрузка',
} as const;
export const VariantRow = z.object({
  /** null — текущий график */
  scenarioId: z.string().nullable(),
  name: z.string(),
  finish: z.string(),
  shiftDays: z.number(),
  /** Укладывается в директивный срок */
  feasible: z.boolean(),
  /** Наибольшая потребность людей и техники в день */
  peak: z.number(),
  /** Неровность загрузки: отклонение от средней, % (меньше — ровнее) */
  unevenness: z.number(),
  critical: z.number(),
  rank: z.number(),
});
export const VariantReport = z.object({
  criterion: z.enum(['finish', 'peak', 'smooth']),
  mustFinishBy: z.string().nullable(),
  rows: z.array(VariantRow),
});
export type VariantReport = z.infer<typeof VariantReport>;

/* ── Освоенный объём (ПЛ-19) ── */
export const EvLine = z.object({
  key: z.string(),
  name: z.string(),
  /** Бюджет по базовому плану */
  bac: z.number(),
  pv: z.number(),
  ev: z.number(),
  ac: z.number(),
  sv: z.number(),
  cv: z.number(),
  spi: z.number().nullable(),
  cpi: z.number().nullable(),
  /** Прогноз затрат на завершение: по CPI */
  eac: z.number().nullable(),
  etc: z.number().nullable(),
  /** Какой CPI нужен на остаток, чтобы уложиться в бюджет */
  tcpi: z.number().nullable(),
});
export const EarnedValue = z.object({
  date: z.string(),
  baseline: z.object({ id: z.string(), name: z.string() }).nullable(),
  total: EvLine,
  /** Три способа прогноза затрат: по CPI, по CPI и SPI, по оставшемуся плану */
  forecasts: z.object({
    byCpi: z.number().nullable(),
    byCpiSpi: z.number().nullable(),
    byPlan: z.number().nullable(),
  }),
  /** Прогноз окончания по SPI */
  finishForecast: z.string().nullable(),
  finishBaseline: z.string().nullable(),
  groups: z.array(EvLine),
  /** S-кривые нарастающим итогом по неделям */
  curve: z.array(
    z.object({ week: z.string(), pv: z.number(), ev: z.number().nullable(), ac: z.number().nullable() }),
  ),
  /** Работы без стоимости в базовом плане: в расчёт не входят */
  withoutCost: z.number(),
  /** Факт затрат: люди по табелю и техника по путевым листам по ставкам; материалы — после склада */
  acSources: z.array(z.string()),
  /** Ресурсы без ставки на день: их факт не учтён */
  missingRates: z.array(z.string()),
});
export type EarnedValue = z.infer<typeof EarnedValue>;

/* ── Графики субподрядчиков (ПЛ-32) ── */
export const CONTRACTOR_UPDATE_STATUSES = {
  pending: 'Ждёт проверки',
  accepted: 'Принят',
  rejected: 'Отклонён',
  superseded: 'Заменён новым',
} as const;
/** Отчёт подрядчика о выполнении своей работы: генподрядчик проверяет и принимает в график */
export const ContractorUpdateInput = z
  .object({
    actualStart: Moment.nullable().optional(),
    actualFinish: Moment.nullable().optional(),
    percent: z.number().min(0).max(100).nullable().optional(),
    /** Осталось рабочих дней */
    remainingDays: z.number().min(0).max(3650).nullable().optional(),
    actualQuantity: z.number().min(0).max(1e12).nullable().optional(),
    comment: z.string().trim().max(1000).nullable().optional(),
  })
  .refine(
    (v) =>
      v.actualStart != null ||
      v.actualFinish != null ||
      v.percent != null ||
      v.remainingDays != null ||
      v.actualQuantity != null,
    { message: 'Укажите хотя бы одно: начало, окончание, процент, остаток или объём' },
  );
export const ContractorActivity = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  wbsPath: z.string().nullable(),
  start: z.string().nullable(),
  finish: z.string().nullable(),
  /** По основному базовому плану */
  blStart: z.string().nullable(),
  blFinish: z.string().nullable(),
  /** Окончание позже базового плана, календарных дней (меньше 0 — раньше) */
  varianceDays: z.number().nullable(),
  actualStart: z.string().nullable(),
  actualFinish: z.string().nullable(),
  percent: z.number(),
  remainingDays: z.number().nullable(),
  quantity: z.number().nullable(),
  unit: z.string().nullable(),
  actualQuantity: z.number().nullable(),
  status: z.enum(['not_started', 'in_progress', 'done']),
  /** Шаги выполнения: процент считается по ним, вручную не вводится */
  hasSteps: z.boolean(),
  pending: z
    .object({
      id: z.string(),
      at: z.string(),
      actualStart: z.string().nullable(),
      actualFinish: z.string().nullable(),
      percent: z.number().nullable(),
      remainingDays: z.number().nullable(),
      actualQuantity: z.number().nullable(),
      comment: z.string().nullable(),
    })
    .nullable(),
  /** Последнее решение генподрядчика */
  last: z
    .object({ status: z.enum(['accepted', 'rejected']), at: z.string(), note: z.string().nullable() })
    .nullable(),
});
export type ContractorActivity = z.infer<typeof ContractorActivity>;
export const ContractorSchedule = z.object({
  projects: z.array(
    z.object({
      projectId: z.string(),
      projectCode: z.string(),
      projectName: z.string(),
      scheduleId: z.string(),
      dataDate: z.string().nullable(),
      baselineName: z.string().nullable(),
      activities: z.array(ContractorActivity),
    }),
  ),
});
export type ContractorSchedule = z.infer<typeof ContractorSchedule>;
export const ContractorUpdateRow = z.object({
  id: z.string(),
  activityId: z.string(),
  activityCode: z.string(),
  activityName: z.string(),
  contractorId: z.string(),
  contractorName: z.string(),
  submittedBy: z.string().nullable(),
  at: z.string(),
  status: z.enum(['pending', 'accepted', 'rejected', 'superseded']),
  actualStart: z.string().nullable(),
  actualFinish: z.string().nullable(),
  percent: z.number().nullable(),
  remainingDays: z.number().nullable(),
  actualQuantity: z.number().nullable(),
  comment: z.string().nullable(),
  /** Что сейчас в графике — для сравнения */
  current: z.object({
    actualStart: z.string().nullable(),
    actualFinish: z.string().nullable(),
    percent: z.number(),
    actualQuantity: z.number().nullable(),
  }),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
});
export type ContractorUpdateRow = z.infer<typeof ContractorUpdateRow>;
export const ContractorsReport = z.object({
  baseline: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** Вклад в перенос посчитан (нет — если график большой и расчёт не уложился) */
  impactReady: z.boolean(),
  contractors: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      total: z.number(),
      done: z.number(),
      inProgress: z.number(),
      /** Выполнение по длительности, % */
      percent: z.number(),
      finish: z.string().nullable(),
      blFinish: z.string().nullable(),
      varianceDays: z.number().nullable(),
      /** Работ, которые заканчиваются позже базового плана */
      late: z.number(),
      /** Сумма вкладов работ в перенос окончания проекта, раб. дн */
      impactDays: z.number().nullable(),
      pending: z.number(),
      /** Участники-подрядчики с доступом к кабинету */
      members: z.number(),
      activities: z.array(
        z.object({
          id: z.string(),
          code: z.string(),
          name: z.string(),
          finish: z.string().nullable(),
          blFinish: z.string().nullable(),
          varianceDays: z.number().nullable(),
          impactDays: z.number().nullable(),
          reasons: z.array(z.string()),
          status: z.enum(['not_started', 'in_progress', 'done']),
        }),
      ),
    }),
  ),
});
export type ContractorsReport = z.infer<typeof ContractorsReport>;
